import { useMemo, type ReactNode } from 'react';
import { AuthProvider as OidcProvider, useAuth } from 'react-oidc-context';
import { AUTH_MODE } from './mode';
import { getUserManager } from './userManager';
import { AuthStateContext, type AuthStateValue } from './authState';
import { resolveReturnTo } from './returnTo';

/** Bridges react-oidc-context into our AuthStateContext. */
function OidcBridge({ children }: Readonly<{ children: ReactNode }>) {
  const auth = useAuth();

  // Token syncing is handled OUTSIDE render so this stays a pure component:
  //  • first paint — main.tsx seeds the token from any still-valid stored session
  //    before React mounts (see primeAccessToken), so the initial /me has a bearer;
  //  • signin / silent-renew / logout — the userManager addUserLoaded /
  //    addUserUnloaded events keep it in lockstep;
  //  • a mid-session 401 — the baseQuery silent-renew path repairs it.
  // Writing the token here during render would be an impure side effect (unsafe
  // under concurrent rendering, where a render can be discarded before commit); an
  // effect would run too late — child effects fire bearer-less requests first.
  //
  // Memoised because RequireAuth's effect depends on signinRedirect: rebuilding the value on every
  // render would hand it a new function each time, and an unrelated re-render would then send the
  // visitor to the IdP a second time, cancelling the first request mid-flight.
  const { isAuthenticated, isLoading, signinRedirect, signoutRedirect, user } = auth;
  const value: AuthStateValue = useMemo(() => ({
    isAuthenticated,
    isLoading,
    // url_state is oidc-client-ts's own round-trip channel: it goes out on the signin request
    // and comes back on the User, so the destination survives a login the visitor never asked
    // for. resolveReturnTo treats what comes back as input, not as something we still own.
    signinRedirect: (returnTo?: string) => void signinRedirect(returnTo ? { url_state: returnTo } : undefined),
    signoutRedirect: () => void signoutRedirect(),
    returnTo: resolveReturnTo(user?.url_state),
  }), [isAuthenticated, isLoading, signinRedirect, signoutRedirect, user]);
  return <AuthStateContext.Provider value={value}>{children}</AuthStateContext.Provider>;
}

/** dev/msw modes: treat the user as authenticated with no OIDC. */
const STATIC_AUTHED: AuthStateValue = {
  isAuthenticated: true,
  isLoading: false,
  signinRedirect: () => {},
  signoutRedirect: () => {},
};

export function AuthProvider({ children }: Readonly<{ children: ReactNode }>) {
  if (AUTH_MODE === 'oidc') {
    const userManager = getUserManager();
    return (
      <OidcProvider
        userManager={userManager}
        onSigninCallback={() => {
          globalThis.history.replaceState({}, document.title, globalThis.location.pathname);
        }}
      >
        <OidcBridge>{children}</OidcBridge>
      </OidcProvider>
    );
  }
  return <AuthStateContext.Provider value={STATIC_AUTHED}>{children}</AuthStateContext.Provider>;
}
